import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Clock, Pencil } from 'lucide-react'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { voterDetail } from '@/features/voters/api'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, humanise } from '@/shared/lib/format'
import type { TransferRequestResponse } from '@/shared/api/types'
import { ConsentPanel } from './ConsentPanel'
import { EditDraftDialog } from './EditDraftDialog'
import { EligibilityPanel } from './EligibilityPanel'
import { UnitPath } from '@/features/geography/UnitPath'
import { TransferActions } from './TransferActions'
import { TransferNotes } from './TransferNotes'
import {
  DECISION_LABEL, INTAKE_CHANNEL_LABEL, STATUS_TONE, TRANSFER_REASON_LABEL,
  TRANSFER_STATUS_LABEL, getTransfer,
} from './api'

/**
 * One transfer request, in full.
 *
 * The page is arranged as the argument a reviewer has to make: **who asked, what
 * they consented to, what was checked, who decided what.** Anyone approving this
 * is signing their name to it, so everything they would need is on one screen
 * and nothing is behind a tab that could be skipped.
 */
export function TransferDetailPage() {
  const { id = '' } = useParams()
  const canRead = useCan('relocation:read')
  const canCreate = useCan('relocation:create')

  const detail = useLoad(() => getTransfer(id), [id])
  const d = detail.data
  const [editing, setEditing] = useState(false)

  if (!canRead) return <Forbidden what="transfers" />
  if (detail.loading && !d) return <Panel><LoadingBlock label="Loading the request…" /></Panel>
  if (!d) {
    return (
      <div className="space-y-4">
        <ErrorStrip message={detail.error} />
        <Panel><EmptyState title="Not found" hint="This request no longer exists, or is outside your scope." /></Panel>
      </div>
    )
  }

  const r = d.request
  const isRegistration = r.kind === 'REGISTRATION'
  const reload = () => void detail.refresh()

  return (
    <div className="space-y-4">
      <SectionHeader
        title={r.reference}
        description={`${TRANSFER_REASON_LABEL[r.reason] ?? humanise(r.reason)} · ${INTAKE_CHANNEL_LABEL[r.intakeChannel] ?? humanise(r.intakeChannel)} intake`}
        right={
          <div className="flex gap-1.5">
            {/* Editing is not a state transition, so it is not in
                `allowedDecisions`; the API's rule is "DRAFT only" and that is
                what this reads. Everything that IS a transition still comes from
                the server — see TransferActions. */}
            {canCreate && r.status === 'DRAFT' && (
              <button type="button" className="erp-btn" aria-label="Correct draft"
                      onClick={() => setEditing(true)}>
                <Pencil className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Correct draft</span>
              </button>
            )}
            <Link to="/transfers" className="erp-btn" aria-label="All requests">
              <ArrowLeft className="h-3.5 w-3.5" /> <span className="axis-hide-sm">All requests</span>
            </Link>
          </div>
        }
      />

      {editing && (
        <EditDraftDialog
          request={r}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); reload() }}
        />
      )}

      <ErrorStrip message={detail.error} />

      {/* The one thing that must never be misread on this page. */}
      {r.status === 'RECORDED' && (
        <Note tone="good">
          Recorded on our register {r.recordedAt ? fmtDateTime(r.recordedAt) : ''}. This records a
          transfer the IEBC performed — the campaign did not move this voter.
        </Note>
      )}

      <KpiStrip>
        <MetricCard
          label="Status"
          value={TRANSFER_STATUS_LABEL[r.status] ?? humanise(r.status)}
          accent={STATUS_TONE[r.status] === 'good'}
          sub={r.substatus ? humanise(r.substatus) : undefined}
        />
        <MetricCard label="Priority" value={r.priority} />
        <MetricCard
          label="SLA"
          value={r.slaDueAt ? (r.slaBreached ? 'Breached' : 'Within') : '—'}
          sub={r.slaDueAt ? `due ${fmtDateTime(r.slaDueAt)}` : undefined}
        />
        <MetricCard
          label="Escalations"
          value={String(r.escalationLevel)}
          sub={r.expiresAt ? `expires ${fmtDateTime(r.expiresAt)}` : undefined}
        />
      </KpiStrip>

      {r.slaBreached && (
        <Note tone="bad">
          <span className="inline-flex items-start gap-1.5">
            <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              This request has passed its service-level deadline for
              {' '}<strong>{TRANSFER_STATUS_LABEL[r.status] ?? humanise(r.status)}</strong>. Someone is
              waiting on it.
            </span>
          </span>
        </Note>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title="The request">
          <dl className="space-y-0">
            <Row label="Voter" value={<VoterLine request={r} />} />
            {isRegistration ? (
              <Row label="Currently" value={
                <span className="text-muted-foreground">
                  Not on the register — there is no station to move them from
                </span>
              } />
            ) : (
              <Row label="From" value={
                <UnitPath stationId={r.fromStationId} path={r.fromUnitPath} />
              } />
            )}
            <Row label={isRegistration ? 'To be registered at' : 'To'}
                 value={<UnitPath stationId={r.toStationId} path={r.toUnitPath} />} />
            <Row label="Raised" value={fmtDateTime(r.createdAt)} />
            {r.iebcReference && (
              <Row label="IEBC reference" value={<code className="font-mono text-erp-xs">{r.iebcReference}</code>} />
            )}
            {r.appealOfId && (
              <Row label="Appeal of" value={
                <Link className="text-chocolate hover:underline" to={`/transfers/${r.appealOfId}`}>
                  the original request
                </Link>
              } />
            )}
            {r.batchId && (
              <Row label="Batch" value={
                <Link className="text-chocolate hover:underline" to={`/transfers?view=batches&open=${r.batchId}`}>
                  part of a batch
                </Link>
              } />
            )}
            {r.placementHistoryId && (
              <Row label="Placement entry" value={
                <code className="font-mono text-erp-xs break-all">{r.placementHistoryId}</code>
              } />
            )}
          </dl>

          <div className="mt-4 pt-3 border-t border-border">
            <div className="text-erp-xs text-muted-foreground mb-1.5">What can happen next</div>
            <TransferActions request={r} allowed={d.allowedDecisions} onDone={reload} />
          </div>
        </Panel>

        <ConsentPanel
          requestId={r.id}
          consent={d.consent}
          canCapture={canCreate}
          allowed={d.allowedDecisions}
          onChanged={reload}
        />
      </div>

      <EligibilityPanel requestId={r.id} kind={r.kind} gates={d.gates} onChanged={reload} />

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title="Decisions" description="Append-only. Every transition, with who made it.">
          {d.decisions.length === 0 ? (
            <p className="text-erp-sm text-muted-foreground">Nothing decided yet.</p>
          ) : (
            <ol className="space-y-2.5">
              {d.decisions.map((dec) => (
                <li key={dec.id} className="flex items-start gap-2.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-chocolate/60 shrink-0 mt-1.5" />
                  <div className="min-w-0">
                    <div className="text-erp-sm">
                      <span className="font-medium">
                        {DECISION_LABEL[dec.decision] ?? humanise(dec.decision)}
                      </span>
                      {dec.fromStatus && dec.toStatus && (
                        <span className="text-erp-xs text-muted-foreground ml-1.5">
                          {humanise(dec.fromStatus)} → {humanise(dec.toStatus)}
                        </span>
                      )}
                    </div>
                    <div className="text-erp-xs text-muted-foreground">
                      {fmtDateTime(dec.decidedAt)}
                      {dec.actorRole ? ` · ${dec.actorRole}` : ''}
                    </div>
                    {dec.notes && <p className="text-erp-sm mt-0.5">{dec.notes}</p>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Panel>

        <TransferNotes requestId={r.id} notes={d.notes} onChanged={reload} />
      </div>
    </div>
  )
}

/**
 * A reviewer is deciding about a **person**, so the person's name belongs on the
 * screen — an id is not something anyone can sanity-check. The lookup is a
 * separate call because the transfer API returns the id alone, and it is allowed
 * to fail: `relocation:read` does not imply `voter:read`, and when it does not
 * the id is still shown rather than an error.
 */
/**
 * The person, not their uuid.
 *
 * `voterName` and friends are resolved server-side (prompt 28) and are the fast path.
 * On an older backend they are absent and this falls back to one lookup — which needs
 * `voter:read`, and when the viewer does not hold it the id is shown rather than an
 * error, because a reviewer who cannot see the name still has to see the request.
 */
function VoterLine({ request }: { request: TransferRequestResponse }) {
  const server = request.voterName
  const v = useLoad(() => (server ? Promise.resolve(null) : voterDetail(request.voterId)),
    [request.voterId, server])
  const name = server ?? v.data?.displayName
  const number = request.voterNumber ?? v.data?.voterNumber

  return (
    <Link className="text-chocolate hover:underline" to={`/voters?open=${request.voterId}`}>
      {name ? (
        <>
          {name}
          {number && (
            <span className="text-muted-foreground font-mono text-erp-xs ml-1.5">{number}</span>
          )}
          {request.nationalIdMasked && (
            <span className="text-muted-foreground font-mono text-erp-xs ml-1.5">
              ID {request.nationalIdMasked}
            </span>
          )}
        </>
      ) : (
        <span className="font-mono text-erp-xs">{request.voterId}</span>
      )}
    </Link>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border last:border-0">
      <dt className="text-erp-sm text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-erp-sm text-right min-w-0">{value}</dd>
    </div>
  )
}

